import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

const FREQS = ['monthly', 'biweekly', 'weekly', 'yearly', 'once'];

const annualized = (entry) => {
  const freq = { monthly: 12, biweekly: 26, weekly: 52, yearly: 1, once: 1 };
  return entry.amount * (freq[entry.frequency] || 12);
};

export default function IncomePage() {
  const [entries, setEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ label: '', amount: '', frequency: 'monthly', start_date: '' });

  const load = async () => setEntries(await api.getIncome());
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.addIncome({ ...form, amount: parseFloat(form.amount), start_date: form.start_date || undefined });
      toast.success('Income source added');
      setShowModal(false);
      setForm({ label: '', amount: '', frequency: 'monthly', start_date: '' });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    await api.deleteIncome(id);
    toast.success('Removed');
    load();
  };

  const totalAnnual = entries.reduce((s, e) => s + annualized(e), 0);
  const totalMonthly = totalAnnual / 12;

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="animate-in" style={{ maxWidth: 680 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Income Sources</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Used to project your monthly cash flow</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Add Income</button>
      </div>

      <div className="grid-2 mb-6">
        <div className="card">
          <div className="stat-label">Monthly Income</div>
          <div className="stat-value green">{fmt(totalMonthly)}</div>
        </div>
        <div className="card">
          <div className="stat-label">Annual Income</div>
          <div className="stat-value gold">{fmt(totalAnnual)}</div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th>Frequency</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ textAlign: 'right' }}>Monthly Equiv.</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                No income sources yet. Add your salary or other income streams.
              </td></tr>
            ) : entries.map(e => (
              <tr key={e.id}>
                <td style={{ fontWeight: 500 }}>{e.label}</td>
                <td style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{e.frequency}</td>
                <td className="text-right amount-pos">{fmt(e.amount)}</td>
                <td className="text-right" style={{ color: 'var(--accent)', fontWeight: 500 }}>{fmt(annualized(e) / 12)}</td>
                <td><button className="btn btn-sm btn-danger" onClick={() => del(e.id)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">Add Income Source</div>
            <div className="form-group">
              <label className="form-label">Label</label>
              <input className="input" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Salary, Freelance, Rental Income" />
            </div>
            <div className="grid-2">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Amount ($)</label>
                <input className="input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Frequency</label>
                <select className="input" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                  {FREQS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group mt-4">
              <label className="form-label">Start Date (optional)</label>
              <input className="input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={!form.label || !form.amount}>Add Income</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
